/* Name a workspace and create it — STORY-004's create route.
 *
 * Two fields and a statement, and the asymmetry between them is the point.
 *
 *   Currency is ASKED, because it cannot be changed once Transactions exist and
 *   it is the one setting that cannot be detected reliably.
 *
 *   Timezone is DETECTED and shown for confirmation only. It is editable later
 *   and an Event takes its zone from its Venue, so a picker here would be
 *   friction for a fallback value.
 *
 * The request is authenticated but pre-tenancy: the caller has zero Memberships
 * when it starts, so nothing tenant-scoped may be touched while it is served.
 */

import { createOrganisationRequestSchema } from '@eventuley/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import {
  AuthScreen,
  Button,
  Callout,
  CurrencySelect,
  TextField,
} from '../../../shared/ui/index.js';
import { useCreateOrganisation } from '../api/index.js';

/* The contract's schema minus the timezone, which is not a field: it is
 * attached at submit from the device. Derived rather than re-declared — a rule
 * written twice is a rule that will disagree with itself, and the server's copy
 * is the one that matters. */
const formSchema = createOrganisationRequestSchema.omit({ timezone: true });
type FormValues = z.infer<typeof formSchema>;

export function CreateWorkspaceScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const createOrganisation = useCreateOrganisation();

  /* Read once. The zone does not change while somebody fills in one form, and
   * re-reading it per render would make the rendered value non-deterministic. */
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  /* A 401 is not "something went wrong". It is "you are not signed in", and it
   * is the one failure on this screen the reader can actually act on — the
   * generic callout gives them nothing to do.
   *
   * Found on the first real signup on production, 2026-08-29: verification does
   * not issue a session, the verify screen sent people here anyway, and this
   * screen answered five identical generic errors. The verify screen is fixed;
   * this is the second line, for an expired session or any other route in.
   *
   * In an effect and guarded by a ref, matching the Account screen: navigating
   * during render is not allowed, and without the guard a second pass
   * overwrites the destination. */
  const unauthenticated =
    createOrganisation.error instanceof ApiError && createOrganisation.error.status === 401;
  const bounced = useRef(false);
  useEffect(() => {
    if (!unauthenticated || bounced.current) return;
    bounced.current = true;
    void navigate({ to: '/sign-in', search: { redirect: '/next/create' }, replace: true });
  }, [unauthenticated, navigate]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: { name: '', currency: '' },
  });

  /* `mutate` with callbacks rather than `mutateAsync`: the latter rejects on
   * failure, and although the failure IS handled — rendered from
   * `createOrganisation.error` — the rejection itself would go unhandled. */
  const onSubmit = handleSubmit((values) => {
    createOrganisation.mutate(
      { ...values, timezone },
      {
        onSuccess: ({ membership }) => {
          void navigate({
            to: '/orgs/$organisationId',
            params: { organisationId: membership.organisationId },
          });
        },
      },
    );
  });

  /* A field-scoped failure from the server renders against that field. The API
   * says which one in `fields[].path`, and discarding that makes the reader
   * hunt for what to fix. */
  const serverFieldError = (path: string): string | undefined => {
    const error = createOrganisation.error;
    if (!(error instanceof ApiError)) return undefined;
    return error.fields.some((field) => field.path === path)
      ? t('workspace.create.currency_unknown')
      : undefined;
  };

  const currencyFieldError = errors.currency
    ? t('workspace.create.currency_required')
    : serverFieldError('currency');

  const nameError = errors.name
    ? errors.name.type === 'too_big'
      ? t('workspace.create.name_too_long')
      : t('error.required')
    : undefined;

  return (
    <AuthScreen title={t('workspace.create.title')} brand={t('app.name')}>
      {createOrganisation.isError && !currencyFieldError && !unauthenticated ? (
        <SubmitError error={createOrganisation.error} />
      ) : null}

      {/* `noValidate` so the browser's own bubbles do not pre-empt ours: they
          are not translated, not styled, and not announced consistently. */}
      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('workspace.create.name_label')}
          autoComplete="organization"
          error={nameError}
          {...register('name')}
        />

        <Controller
          control={control}
          name="currency"
          render={({ field }) => (
            <CurrencySelect
              label={t('workspace.create.currency_label')}
              commonLabel={t('workspace.create.currency_common')}
              allLabel={t('workspace.create.currency_all')}
              emptyLabel={t('workspace.create.currency_empty')}
              resultsLabel={(count) =>
                t('workspace.create.currency_results', { count: String(count) })
              }
              hint={t('workspace.create.currency_hint')}
              error={currencyFieldError}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <p className="field__hint">{t('workspace.create.timezone_detected', { timezone })}</p>

        <Button type="submit" block busy={isSubmitting || createOrganisation.isPending}>
          {isSubmitting || createOrganisation.isPending
            ? t('common.submitting')
            : t('workspace.create.submit')}
        </Button>
      </form>

      <p className="auth__alt">
        <Link to="/next">{t('workspace.create.back')}</Link>
      </p>
    </AuthScreen>
  );
}

/** Turns a failure into a sentence in the reader's language. */
function SubmitError({ error }: { readonly error: unknown }) {
  const t = useTranslate();

  /* Branching on the CODE, never on the message. The message is English, is for
   * humans, and is never parsed — `coding.md`. */
  if (error instanceof ApiError && error.code === 'rate_limited') {
    return <Callout tone="warning">{t('error.rate_limited')}</Callout>;
  }
  if (error instanceof NetworkError) {
    return <Callout tone="danger">{t('error.network')}</Callout>;
  }
  return <Callout tone="danger">{t('error.unexpected')}</Callout>;
}
